/**
 * Service-worker registration and the "a new version is waiting" signal.
 *
 * Registration lives here rather than in a component, for the same reason
 * `install.ts` captures `beforeinstallprompt` at module load: it must happen
 * once, early, and independently of what is mounted. The banner that surfaces
 * the signal renders **inside the phone column** (`PhoneFrame`), and a column
 * unmounts and remounts as the user moves between shell and pushed routes — so
 * a component that both registered and rendered would re-register on every
 * navigation. Splitting them makes the banner a pure consumer that can be
 * rendered anywhere, as many times as there are columns.
 *
 * `registerType: 'prompt'` in `vite.config.ts` means a new worker installs and
 * then **waits**. That is the right default for a food diary — swapping the
 * shell mid-entry loses what someone typed — but it only works if something
 * eventually asks, or the user sits on a stale shell forever.
 *
 * SUAP-0106.
 */
// `virtual:pwa-register` is generated by vite-plugin-pwa at build time; it has no
// file on disk, which is why an editor may show it unresolved.
import { registerSW } from 'virtual:pwa-register'

let waiting = false
let applyUpdate: (() => Promise<void>) | null = null
const listeners = new Set<() => void>()

const notify = () => {
  for (const listener of listeners) listener()
}

if (typeof window !== 'undefined') {
  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      waiting = true
      applyUpdate = () => updateSW(true)
      notify()
    },
    onRegisteredSW(_url, registration) {
      if (registration === undefined) return
      // Nothing polls for a new worker on its own, so an installed app left open
      // never learns about a deploy until the next navigation. Checking when the
      // tab is brought back to the front is the cheapest honest approximation of
      // "the user has just come back to us".
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void registration.update()
      })
    },
  })
}

/**
 * `localStorage['suap.forceUpdateBanner']` renders the banner without a deploy.
 *
 * This exists because **no check had ever rendered the banner**, so every
 * `check:a11y` result for it was a banner-down result — and that blind spot is
 * how it shipped pushing all four tabs off the bottom of the screen
 * (`docs/coverage/EPIC-01.md § SUAP-0106`). A waiting service worker cannot be
 * faked from outside the page, and a build-time flag would prove nothing about
 * the production bundle CI actually ships, so the hook is here, in that bundle.
 *
 * Read through the same try/catch as the theme and locale readers: storage throws
 * in a locked-down context, and a debug flag is not worth a white screen. The
 * worst a user can do with it is see a truthful reload prompt and reload.
 *
 * SUAP-0113.
 */
const FORCE_KEY = 'suap.forceUpdateBanner'

function forced(): boolean {
  try {
    return window.localStorage.getItem(FORCE_KEY) === '1'
  } catch {
    return false
  }
}

export function updateWaiting(): boolean {
  return waiting || forced()
}

export function onUpdateWaiting(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Apply the waiting update and reload.
 *
 * `updateSW(true)` tells the waiting worker to take over and reloads when it
 * does. On a page the worker does **not** yet control — the very first visit,
 * before any reload — `controlling` never fires, so that promise never resolves
 * and the button would silently do nothing. A plain reload is the correct
 * fallback there: it is what puts the new worker in charge anyway.
 */
export async function applyWaitingUpdate(): Promise<void> {
  const controlled =
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    navigator.serviceWorker.controller !== null

  if (applyUpdate === null || !controlled) {
    window.location.reload()
    return
  }
  await applyUpdate()
}
